// src/pages/AddGuestPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Restaurant as RestaurantIcon,
  LocalBar as LocalBarIcon,
  Spa as SpaIcon,
  FitnessCenter as FitnessCenterIcon,
  Movie as MovieIcon,
  ShoppingBag as ShoppingBagIcon,
  Explore as ExploreIcon,
  Egg as EggIcon,
  LocalFlorist as LocalFloristIcon,
  AcUnit as AcUnitIcon,
  LocalDrink as LocalDrinkIcon,
  BakeryDining as BakeryDiningIcon,
  SetMeal as SetMealIcon,
  LunchDining as LunchDiningIcon,
  LightMode,
  DarkMode,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { countries } from '../shared/countries';
import { validateEmail, validateTCKN, validatePassport, isTurkishNationality } from '../shared/validators';

const PRODUCT_INTEREST_BASE = [
  { value: 'food', icon: <RestaurantIcon />, color: '#f59e0b' },
  { value: 'beverage', icon: <LocalBarIcon />, color: '#8b5cf6' },
  { value: 'spa', icon: <SpaIcon />, color: '#ec4899' },
  { value: 'fitness', icon: <FitnessCenterIcon />, color: '#10b981' },
  { value: 'entertainment', icon: <MovieIcon />, color: '#3b82f6' },
  { value: 'shopping', icon: <ShoppingBagIcon />, color: '#ef4444' },
  { value: 'tours', icon: <ExploreIcon />, color: '#06b6d4' },
];

const ALLERGY_BASE = [
  { value: 'eggs', icon: <EggIcon />, color: '#f59e0b' },
  { value: 'flowers', icon: <LocalFloristIcon />, color: '#ec4899' },
  { value: 'nuts', icon: <AcUnitIcon />, color: '#8b5cf6' },
  { value: 'dairy', icon: <LocalDrinkIcon />, color: '#3b82f6' },
  { value: 'gluten', icon: <BakeryDiningIcon />, color: '#f97316' },
  { value: 'seafood', icon: <SetMealIcon />, color: '#06b6d4' },
  { value: 'soy', icon: <LunchDiningIcon />, color: '#10b981' },
];

const AddGuestPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accentColor = '#00AEEF';
  const accentHover = '#0095cf';
  const paperBg = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.88) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.97) 0%, rgba(255, 255, 255, 0.92) 100%)';
  const paperBorder = isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.04)';
  const glassBg = isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.6)';
  const glassBorder = isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid rgba(0, 0, 0, 0.08)';
  const primaryTextColor = isDark ? '#f8fafc' : '#1f2937';
  const handleThemeToggle = () => {
    const newMode = isDark ? 'light' : 'dark';
    localStorage.setItem('themeMode', newMode);
    window.dispatchEvent(new CustomEvent('themeChange', { detail: newMode }));
  };
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [capacityInfo, setCapacityInfo] = useState<{
    currentGuestCount: number;
    maxGuestCount: number;
    roomNumber: string | null;
  } | null>(null);

  const hotelId = searchParams.get('hotelId');
  const reservationId = searchParams.get('reservationId');
  const surname = searchParams.get('surname');
  const redirectToChat = searchParams.get('redirectToChat') === 'true';

  const isRoomFull = !!(
    capacityInfo
    && capacityInfo.maxGuestCount > 0
    && capacityInfo.currentGuestCount >= capacityInfo.maxGuestCount
  );

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    birthdate: '',
    nationalId: '',
    nationality: '',
    country: '',
    city: '',
    postCode: '',
    gender: '',
    kvkk: false,
    passportId: '',
    productInterests: [] as string[],
    allergies: [] as string[],
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [gdprText, setGdprText] = useState('');
  const [gdprDialogOpen, setGdprDialogOpen] = useState(false);

  useEffect(() => {
    if (!hotelId) return;
    const lang = i18n.language?.split('-')[0] || 'tr';
    api
      .get(`/hotels/${hotelId}/legal-text`, { params: { lang } })
      .then((res) => setGdprText(res.data?.text || ''))
      .catch(() => setGdprText(''));
  }, [hotelId, i18n.language]);

  useEffect(() => {
    // Validate that required query parameters are present
    if (!hotelId || !reservationId) {
      setError(t('login.failedToAddGuest') || 'Missing required parameters. Please go back and try again.');
    }
  }, [hotelId, reservationId, t]);

  // Fetch the room's max guest count vs current count so we can prevent
  // the operator from adding more guests than the PMS allows.
  useEffect(() => {
    if (!hotelId || !reservationId) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await api.get('/auth/reservation-capacity', {
          params: { hotelId, reservationId },
        });
        if (cancelled) return;
        setCapacityInfo({
          currentGuestCount: response.data?.currentGuestCount ?? 0,
          maxGuestCount: response.data?.maxGuestCount ?? 0,
          roomNumber: response.data?.roomNumber ?? null,
        });
      } catch (err) {
        console.warn('[AddGuestPage] Failed to fetch reservation capacity:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hotelId, reservationId]);

  // Get country name based on current language
  const getCountryName = (country: typeof countries[0]) => {
    const lang = i18n.language.split('-')[0];
    switch (lang) {
      case 'tr': return country.nameTr || country.name;
      case 'de': return country.nameDe || country.name;
      case 'fr': return country.nameFr || country.name;
      case 'ru': return country.nameRu || country.name;
      case 'ar': return country.nameAr || country.name;
      default: return country.name;
    }
  };

  // Validation functions
  const validateName = (value: string): boolean => {
    // Only allow letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-ZÀ-ÿĀ-žА-яА-Я\s'-]+$/;
    if (!nameRegex.test(value)) {
      return false;
    }
    return true;
  };

  const validatePhone = (value: string): boolean => {
    // Only allow + at the beginning, then digits, no spaces
    const phoneRegex = /^\+?[0-9]*$/;
    if (value.includes(' ')) {
      return false;
    }
    if (value.startsWith('+') && value.length > 1) {
      return phoneRegex.test(value);
    }
    if (!value.startsWith('+')) {
      return phoneRegex.test(value);
    }
    return true;
  };

  const validateBirthdate = (value: string): boolean => {
    if (!value) return true; // Optional field
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today;
  };

  const handleChange = (field: string, value: any) => {
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Apply validation based on field type
    if (field === 'firstName' || field === 'lastName') {
      if (value && !validateName(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('validation.onlyLetters')
        }));
        return;
      }
    }

    if (field === 'phoneNumber') {
      if (value && !validatePhone(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('validation.phoneFormat')
        }));
        return;
      }
    }

    if (field === 'birthdate') {
      if (value && !validateBirthdate(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('validation.birthDateFuture')
        }));
        return;
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setFieldErrors({});

    // Validate all fields
    const errors: Record<string, string> = {};

    if (!formData.firstName || !validateName(formData.firstName)) {
      errors.firstName = t('validation.firstNameRequired');
    }

    if (!formData.lastName || !validateName(formData.lastName)) {
      errors.lastName = t('validation.lastNameRequired');
    }

    if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
      errors.phoneNumber = t('validation.invalidPhoneFormat');
    }

    if (formData.birthdate && !validateBirthdate(formData.birthdate)) {
      errors.birthdate = t('validation.birthDateFuture');
    }

    if (formData.nationality && !countries.find(c => c.code === formData.nationality)) {
      errors.nationality = t('validation.selectValidNationality');
    }

    if (formData.country && !countries.find(c => c.code === formData.country)) {
      errors.country = t('validation.selectValidCountry');
    }

    if (formData.email && !validateEmail(formData.email)) {
      errors.email = t('validation.invalidEmail');
    }

    // National ID (T.C.) required for Turkish nationality; passport otherwise.
    const isTR = isTurkishNationality(formData.nationality);
    if (formData.nationality && formData.nationality.trim()) {
      if (isTR) {
        if (!formData.nationalId || !formData.nationalId.trim()) {
          errors.nationalId = t('validation.nationalIdRequired');
        } else if (!validateTCKN(formData.nationalId)) {
          errors.nationalId = t('validation.invalidTckn');
        }
      } else if (!formData.passportId || !formData.passportId.trim()) {
        errors.passportId = t('validation.passportRequired');
      } else if (!validatePassport(formData.passportId, formData.nationality)) {
        errors.passportId = t('validation.invalidPassport');
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setError(t('login.firstNameLastNameRequired'));
      return;
    }

    if (!hotelId || !reservationId) {
      setError(t('login.failedToAddGuest') || 'Missing required parameters');
      return;
    }

    if (!formData.kvkk) {
      setError(t('login.kvkkRequired') || 'KVKK consent is required');
      return;
    }

    if (isRoomFull) {
      setError(
        t('addGuest.roomFullError', {
          current: capacityInfo?.currentGuestCount,
          max: capacityInfo?.maxGuestCount,
          defaultValue: `Room is at full capacity (${capacityInfo?.currentGuestCount}/${capacityInfo?.maxGuestCount}). No more guests can be added.`,
        }),
      );
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post('/auth/add-guest', {
        hotelId,
        reservationId,
        ...formData,
      });

      setSuccess(true);
      
      // Redirect to onboarding page after a short delay
      setTimeout(() => {
        const params = new URLSearchParams({
          hotelId: hotelId!,
          reservationId: reservationId!,
          profileId: response.data.profileId,
        });
        if (surname) {
          params.append('surname', surname);
        }
        if (redirectToChat) {
          params.append('redirectToChat', 'true');
        }
        // Yeni eklenen misafirin soyadı - "Bizimle yazışmaya başlayın" ile chat'e girerken selectGuest için gerekli
        params.append('guestLastName', formData.lastName);
        navigate(`/guest-onboarding?${params.toString()}`);
      }, 1500);
    } catch (error: any) {
      console.error('Failed to add guest:', error);
      setError(error.response?.data?.message || t('login.failedToAddGuest') || 'Failed to add guest. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (hotelId && reservationId) {
      const params = new URLSearchParams({
        hotelId: hotelId,
        reservationId: reservationId,
        showGuestSelection: 'true',
      });
      if (surname) {
        params.append('surname', surname);
      }
      if (redirectToChat) {
        params.append('redirectToChat', 'true');
      }
      navigate(`/login?${params.toString()}`);
    } else {
      navigate('/login');
    }
  };

  const productInterestOptions = useMemo(
    () =>
      PRODUCT_INTEREST_BASE.map((item) => ({
        ...item,
        label: t(`addGuest.productInterests.${item.value}`),
      })),
    [t],
  );

  const allergyOptions = useMemo(
    () =>
      ALLERGY_BASE.map((item) => ({
        ...item,
        label: t(`addGuest.allergies.${item.value}`),
      })),
    [t],
  );

  // Common TextField styling - matching login page
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 3,
      backgroundColor: isDark ? 'rgba(15, 23, 42, 0.65)' : 'rgba(255, 255, 255, 0.9)',
      color: isDark ? '#f8fafc' : '#1f2937',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '& fieldset': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
        borderWidth: 1.5,
        transition: 'all 0.3s ease',
      },
      '&:hover': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.78)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark
          ? '0 8px 22px rgba(0, 0, 0, 0.45)'
          : '0 4px 12px rgba(0, 174, 239, 0.12)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2,
        },
      },
      '&.Mui-focused': {
        backgroundColor: isDark ? 'rgba(15, 23, 42, 0.9)' : '#ffffff',
        transform: 'translateY(-1px)',
        boxShadow: isDark
          ? '0 12px 30px rgba(0, 174, 239, 0.3)'
          : '0 8px 20px rgba(0, 174, 239, 0.18)',
        '& fieldset': {
          borderColor: accentColor,
          borderWidth: 2.5,
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: isDark ? 'rgba(248, 250, 252, 0.7)' : '#6b7280',
      '&.Mui-focused': {
        color: accentColor,
        fontWeight: 700,
      },
    },
    '& .MuiInputBase-input': {
      fontWeight: 500,
      color: isDark ? '#f8fafc' : '#1f2937',
    },
  };

  return (
    <>
    <Container maxWidth={false} sx={{ mt: 3, mb: 4, maxWidth: '1000px' }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 3,
          background: paperBg,
          backdropFilter: 'blur(18px)',
          border: paperBorder,
          position: 'relative',
          boxShadow: isDark
            ? '0 25px 70px rgba(0, 0, 0, 0.6)'
            : '0 30px 80px rgba(15, 23, 42, 0.12)',
        }}
      >
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 24,
              zIndex: 1,
            }}
          >
            <Tooltip title={isDark ? t('sidebar.light') : t('sidebar.dark')}>
              <IconButton
                onClick={handleThemeToggle}
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(15, 23, 42, 0.1)'}`,
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.05)',
                  color: isDark ? '#f8fafc' : '#0f172a',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 174, 239, 0.18)',
                    color: '#0f172a',
                  },
                }}
              >
                {isDark ? <LightMode sx={{ fontSize: 22 }} /> : <DarkMode sx={{ fontSize: 22 }} />}
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={isLoading || success}
              sx={{
                mb: 2,
                textTransform: 'none',
              color: accentColor,
                fontWeight: 600,
                '&:hover': {
                backgroundColor: isDark ? 'rgba(0, 174, 239, 0.15)' : 'rgba(0, 174, 239, 0.08)',
                },
              }}
            >
              {t('login.back')}
            </Button>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{ fontWeight: 700, color: accentColor }}
            >
                {t('login.newGuest')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('login.addGuestToRoom')}
              </Typography>
            </Box>
          </Box>

          {capacityInfo && capacityInfo.maxGuestCount > 0 && (
            <Alert
              severity={isRoomFull ? 'error' : 'info'}
              icon={false}
              sx={{
                mb: 3,
                borderRadius: 2.5,
                boxShadow: isRoomFull
                  ? '0 2px 8px rgba(220, 38, 38, 0.15)'
                  : '0 2px 8px rgba(0, 174, 239, 0.15)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t('addGuest.roomCapacityLabel', {
                  current: capacityInfo.currentGuestCount,
                  max: capacityInfo.maxGuestCount,
                  defaultValue: `Guests in this room: ${capacityInfo.currentGuestCount} / ${capacityInfo.maxGuestCount}`,
                })}
                {capacityInfo.roomNumber ? ` · ${t('login.room') || 'Room'} ${capacityInfo.roomNumber}` : ''}
              </Typography>
              {isRoomFull && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {t('addGuest.roomFullHint', {
                    defaultValue: 'The room is full. You cannot add another guest.',
                  })}
                </Typography>
              )}
            </Alert>
          )}

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(220, 38, 38, 0.15)',
                animation: 'slideDown 0.3s ease-out',
                '@keyframes slideDown': {
                  from: {
                    opacity: 0,
                    transform: 'translateY(-10px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
              }}
            >
              {error}
            </Alert>
          )}

          {success && (
            <Alert 
              severity="success" 
              sx={{ 
                mb: 3,
                borderRadius: 2.5,
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
              }}
            >
              {t('validation.guestAddedSuccess')}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 2 }}
          >
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  label={t('login.firstName')}
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  disabled={isLoading || success}
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                  sx={textFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                      <PersonIcon sx={{ color: accentColor, fontSize: '1.25rem' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  required
                  fullWidth
                  label={t('login.lastName')}
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  disabled={isLoading || success}
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="email"
                  label={t('login.email')}
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={isLoading || success}
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('login.phoneNumber')}
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Remove spaces
                    value = value.replace(/\s/g, '');
                    // Only allow + at the beginning
                    if (value.includes('+') && value.indexOf('+') !== 0) {
                      value = value.replace(/\+/g, '');
                    }
                    handleChange('phoneNumber', value);
                  }}
                  disabled={isLoading || success}
                  error={!!fieldErrors.phoneNumber}
                  helperText={fieldErrors.phoneNumber || (t('login.phoneNumber') === 'Phone Number' ? 'Format: +1234567890' : 'Format: +905551234567')}
                  sx={textFieldSx}
                  inputProps={{
                    maxLength: 20,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  type="date"
                  label={t('login.birthdate')}
                  InputLabelProps={{ shrink: true }}
                  value={formData.birthdate}
                  onChange={(e) => handleChange('birthdate', e.target.value)}
                  disabled={isLoading || success}
                  error={!!fieldErrors.birthdate}
                  helperText={fieldErrors.birthdate}
                  sx={textFieldSx}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0], // Prevent future dates
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required={isTurkishNationality(formData.nationality)}
                  label={t('login.nationalId')}
                  value={formData.nationalId}
                  onChange={(e) => handleChange('nationalId', e.target.value)}
                  disabled={isLoading || success}
                  error={!!fieldErrors.nationalId}
                  helperText={fieldErrors.nationalId}
                  inputProps={{ maxLength: 11 }}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth error={!!fieldErrors.nationality} sx={textFieldSx}>
                  <InputLabel>{t('login.nationality')}</InputLabel>
                  <Select
                    value={formData.nationality}
                    label={t('login.nationality')}
                    onChange={(e) => handleChange('nationality', e.target.value)}
                    disabled={isLoading || success}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 3,
                          mt: 1,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {getCountryName(country)}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.nationality && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {fieldErrors.nationality}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth sx={textFieldSx}>
                  <InputLabel>{t('login.gender')}</InputLabel>
                  <Select
                    value={formData.gender}
                    label={t('login.gender')}
                    onChange={(e) => handleChange('gender', e.target.value)}
                    disabled={isLoading || success}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 3,
                          mt: 1,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                        },
                      },
                    }}
                  >
                    <MenuItem value="M">{t('login.male')}</MenuItem>
                    <MenuItem value="F">{t('login.female')}</MenuItem>
                    <MenuItem value="Other">{t('login.other')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth error={!!fieldErrors.country} sx={textFieldSx}>
                  <InputLabel>{t('login.country')}</InputLabel>
                  <Select
                    value={formData.country}
                    label={t('login.country')}
                    onChange={(e) => handleChange('country', e.target.value)}
                    disabled={isLoading || success}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: 3,
                          mt: 1,
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                          maxHeight: 300,
                        },
                      },
                    }}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {getCountryName(country)}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.country && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {fieldErrors.country}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('login.city')}
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  disabled={isLoading || success}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('login.postCode')}
                  value={formData.postCode}
                  onChange={(e) => handleChange('postCode', e.target.value)}
                  disabled={isLoading || success}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required={!!formData.nationality && !isTurkishNationality(formData.nationality)}
                  label={t('login.passportId')}
                  value={formData.passportId}
                  onChange={(e) => handleChange('passportId', e.target.value)}
                  disabled={isLoading || success}
                  error={!!fieldErrors.passportId}
                  helperText={fieldErrors.passportId}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.kvkk}
                      onChange={(e) => handleChange('kvkk', e.target.checked)}
                      disabled={isLoading || success}
                      sx={{
                        color: accentColor,
                        '&.Mui-checked': {
                          color: accentColor,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      {t('login.kvkk')}
                      {gdprText && (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => setGdprDialogOpen(true)}
                          sx={{ ml: 1, verticalAlign: 'baseline' }}
                        >
                          {t('login.viewGdprText')}
                        </Link>
                      )}
                    </Box>
                  }
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: primaryTextColor,
                      mb: 2,
                      fontSize: '0.95rem',
                    }}
                  >
                    {t('login.productInterests')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: glassBg,
                      border: glassBorder,
                      minHeight: 80,
                    }}
                  >
                    {productInterestOptions.map((option) => {
                      const isSelected = formData.productInterests.includes(option.value);
                      return (
                        <Chip
                          key={option.value}
                          icon={option.icon}
                          label={option.label}
                          onClick={() => {
                            if (isLoading || success) return;
                            const newInterests = isSelected
                              ? formData.productInterests.filter((v) => v !== option.value)
                              : [...formData.productInterests, option.value];
                            handleChange('productInterests', newInterests);
                          }}
                          disabled={isLoading || success}
                          sx={{
                            height: 48,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backgroundColor: isSelected
                              ? option.color
                              : (isDark ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.9)'),
                            color: isSelected ? '#ffffff' : (isDark ? '#e2e8f0' : '#374151'),
                            border: isSelected
                              ? `2px solid ${option.color}`
                              : (isDark ? '2px solid rgba(255, 255, 255, 0.12)' : '2px solid rgba(0, 0, 0, 0.12)'),
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${option.color}40`,
                              backgroundColor: isSelected ? option.color : (isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 1)'),
                            },
                            '& .MuiChip-icon': {
                              color: isSelected ? '#ffffff' : option.color,
                              fontSize: '1.5rem',
                            },
                            '&:disabled': {
                              opacity: 0.6,
                              cursor: 'not-allowed',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 700,
                      color: primaryTextColor,
                      mb: 2,
                      fontSize: '0.95rem',
                    }}
                  >
                    {t('login.allergies')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1.5,
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: glassBg,
                      border: glassBorder,
                      minHeight: 80,
                    }}
                  >
                    {allergyOptions.map((option) => {
                      const isSelected = formData.allergies.includes(option.value);
                      return (
                        <Chip
                          key={option.value}
                          icon={option.icon}
                          label={option.label}
                          onClick={() => {
                            if (isLoading || success) return;
                            const newAllergies = isSelected
                              ? formData.allergies.filter((v) => v !== option.value)
                              : [...formData.allergies, option.value];
                            handleChange('allergies', newAllergies);
                          }}
                          disabled={isLoading || success}
                          sx={{
                            height: 48,
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backgroundColor: isSelected
                              ? option.color
                              : (isDark ? 'rgba(15, 23, 42, 0.75)' : 'rgba(255, 255, 255, 0.9)'),
                            color: isSelected ? '#ffffff' : (isDark ? '#e2e8f0' : '#374151'),
                            border: isSelected
                              ? `2px solid ${option.color}`
                              : (isDark ? '2px solid rgba(255, 255, 255, 0.12)' : '2px solid rgba(0, 0, 0, 0.12)'),
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${option.color}40`,
                              backgroundColor: isSelected ? option.color : (isDark ? 'rgba(15, 23, 42, 0.9)' : 'rgba(255, 255, 255, 1)'),
                            },
                            '& .MuiChip-icon': {
                              color: isSelected ? '#ffffff' : option.color,
                              fontSize: '1.5rem',
                            },
                            '&:disabled': {
                              opacity: 0.6,
                              cursor: 'not-allowed',
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 2, mt: 4, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isLoading || success}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                  borderWidth: 1.5,
                  color: '#6b7280',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  backgroundColor: glassBg,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    borderColor: accentColor,
                    borderWidth: 2,
                    backgroundColor: isDark ? 'rgba(15, 23, 42, 0.8)' : 'rgba(0, 0, 0, 0.04)',
                    transform: 'translateY(-1px)',
                    boxShadow: isDark
                      ? '0 6px 18px rgba(0, 0, 0, 0.45)'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                {t('login.back')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading || success || !formData.firstName || !formData.lastName || isRoomFull}
                sx={{
                  px: 4,
                  py: 1.5,
                  backgroundColor: accentColor,
                  boxShadow: '0 18px 35px rgba(0, 174, 239, 0.35)',
                  '&:hover': {
                    backgroundColor: accentHover,
                    boxShadow: '0 18px 40px rgba(0, 174, 239, 0.45)',
                  },
                  '&:disabled': {
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.12)' : '#cbd5e1',
                    boxShadow: 'none',
                    color: isDark ? 'rgba(255, 255, 255, 0.6)' : '#94a3b8',
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : t('login.addGuest')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>

      <Dialog open={gdprDialogOpen} onClose={() => setGdprDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('login.kvkk')}</DialogTitle>
        <DialogContent dividers>
          <Typography component="div" sx={{ whiteSpace: 'pre-wrap' }}>
            {gdprText || t('login.gdprNotConfigured')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGdprDialogOpen(false)}>{t('common.ok') || 'OK'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddGuestPage;
